import {createAction} from 'typesafe-actions';

import {type PanelConfigOverride} from '../../../components/PanelBank/types';
import type * as PanelBankConfigTypes from '../panelBankConfig/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import type * as WorkspaceSettingsTypes from '../workspaceSettings/types';
import type * as Types from './types';

/**
 * Sets the config of a panel. Used as the inverse action for updateConfig.
 * Optionally includes override restoration info for sparse override support.
 *
 * TODO: The positional parameter list here is getting unwieldy (6 params with
 * middle `undefined` placeholders at call sites). We should probably refactor
 * optional params into an options object.
 */
export const setConfig = createAction(
  '@view/panel/setConfig',
  action =>
    (
      ref: Types.Ref,
      config: Types.Panel['config'],
      key?: string,
      overrideInfo?: {
        panelBankConfigRef: PanelBankConfigTypes.Ref;
        panelKey: string;
        prevOverride: PanelConfigOverride | undefined;
      },
      panelBankSectionConfigRef?: PanelBankSectionConfigTypes.Ref,
      panelBankConfigRef?: PanelBankConfigTypes.Ref,
      isPanelsAutoForUndoRedo?: {value: boolean | undefined}
    ) =>
      action({
        ref,
        config,
        key,
        overrideInfo,
        panelBankSectionConfigRef,
        panelBankConfigRef,
        isPanelsAutoForUndoRedo,
      })
);

export const setConfigs = createAction(
  '@view/panel/setConfigs',
  action => (refs: Types.Ref[], configs: Array<Types.Panel['config']>) =>
    action({refs, configs})
);

/**
 * Updates the config of an individual panel.
 */
export const updateConfig = createAction(
  '@view/panel/updateConfig',
  action =>
    (
      ref: Types.Ref,
      configUpdate: Partial<Types.Panel['config']>,
      panelBankSectionConfigRef?: PanelBankSectionConfigTypes.Ref,
      panelBankConfigRef?: PanelBankConfigTypes.Ref,
      options?: {isUndoable?: boolean}
    ) =>
      action({
        ref,
        configUpdate,
        panelBankSectionConfigRef,
        panelBankConfigRef,
        options,
      })
);

export const updateConfigs = createAction(
  '@view/panel/updateConfigs',
  action => (refs: Types.Ref[], configUpdate: Partial<Types.Panel['config']>) =>
    action({refs, configUpdate})
);

export const deletePanels = createAction(
  '@view/panel/deletePanels',
  action =>
    (
      panelRefs: Types.Ref[],
      sectionRef?: PanelBankSectionConfigTypes.Ref,
      panelBankConfigRef?: PanelBankConfigTypes.Ref,
      workspaceSettingsRef?: WorkspaceSettingsTypes.Ref,
      isPanelsAutoForUndoRedo?: {value: boolean | undefined}
    ) =>
      action({
        panelRefs,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForUndoRedo,
      })
);

export const undoDeletePanels = createAction(
  '@view/panel/undoDeletePanels',
  action =>
    (
      deletePanelsResult: Types.DeletePanelsResult,
      sectionRef?: PanelBankSectionConfigTypes.Ref,
      panelBankConfigRef?: PanelBankConfigTypes.Ref,
      workspaceSettingsRef?: WorkspaceSettingsTypes.Ref,
      isPanelsAutoForRedo?: {value: boolean | undefined}
    ) =>
      action({
        deletePanelsResult,
        sectionRef,
        panelBankConfigRef,
        workspaceSettingsRef,
        isPanelsAutoForRedo,
      })
);
